import React from 'react';
import { Phone, MessageCircle, Star, Heart, Eye, Shield, MapPin, Clock } from 'lucide-react';

export const Hero = () => {
  return (
    <section id="accueil" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden pt-20">
      {/* CSS-only Background - No image to load */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-950/40 via-black to-neutral-950"></div>

      {/* Dot Pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 25% 25%, rgb(212, 168, 67) 1px, transparent 1px), radial-gradient(circle at 75% 75%, rgb(212, 168, 67) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }}></div>

      {/* Floating Glow Effects */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600/8 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-3xl"></div>

      {/* Gold Lines */}
      <div className="absolute top-0 left-1/4 w-px h-32 bg-gradient-to-b from-transparent via-amber-500/50 to-transparent"></div>
      <div className="absolute bottom-0 right-1/4 w-px h-32 bg-gradient-to-t from-transparent via-amber-500/50 to-transparent"></div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <div className="inline-flex items-center space-x-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-4" data-testid="hero-badge">
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
          <span className="text-amber-200 text-sm font-medium">Plus de 30 ans d'expérience à <a href="#contact" className="underline hover:text-amber-400">Genève</a></span>
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
          <span className="text-amber-400">Maître Diamma</span>
          <span className="block text-2xl sm:text-3xl md:text-4xl mt-2 text-amber-200/80 font-light">Grand Marabout Africain à Genève</span>
        </h1>

        {/* Subtitle */}
        <h2 className="text-lg sm:text-xl md:text-2xl text-amber-100/70 font-light mb-4">
          <span className="text-amber-400 font-medium">N°1 du Retour Affectif Rapide</span> - Voyant Médium Sérieux
        </h2>

        {/* Description */}
        <p className="text-base sm:text-lg text-amber-100/60 max-w-3xl mx-auto mb-4">
          Spécialiste reconnu : <a href="#services" className="text-amber-400 hover:underline">Retour de l'être aimé</a> • <a href="#services" className="text-amber-400 hover:underline">Voyance</a> • <a href="#services" className="text-amber-400 hover:underline">Désenvoûtement</a> • Résultats en 3 à 7 jours
        </p>

        {/* Star Rating */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
            ))}
          </div>
          <span className="text-amber-200/60">|</span>
          <a href="#temoignages" className="text-amber-300 hover:text-amber-400 transition-colors" data-testid="reviews-link">800+ avis clients</a>
        </div>

        {/* Service Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {['Retour Affectif', 'Voyance', 'Guérison', 'Protection', 'Chance'].map((service) => (
            <a
              key={service}
              href="#services"
              className="text-xs sm:text-sm text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 hover:bg-amber-500/20 hover:border-amber-500/40 transition-all"
            >
              {service}
            </a>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <a
            href="tel:+41767836186"
            className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold px-8 py-4 text-lg rounded-lg shadow-xl shadow-amber-500/30 w-full sm:w-auto transition-all"
            data-testid="hero-call-btn"
          >
            <Phone className="w-5 h-5 mr-2" />
            Appeler Maintenant
          </a>
          <a
            href="https://wa.me/41767836186"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-green-600 hover:bg-green-500 text-white font-bold px-8 py-4 text-lg rounded-lg shadow-xl shadow-green-600/30 w-full sm:w-auto transition-all"
            data-testid="hero-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            WhatsApp
          </a>
        </div>

        {/* Quick Service Icons */}
        <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-12">
          <a href="#services" className="flex flex-col items-center p-4 rounded-xl bg-amber-500/5 border border-amber-500/10 hover:bg-amber-500/10 hover:border-amber-500/30 transition-all group">
            <Heart className="w-6 h-6 text-amber-400 mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-amber-200/80 text-xs">Retour Affectif</span>
          </a>
          <a href="#services" className="flex flex-col items-center p-4 rounded-xl bg-amber-500/5 border border-amber-500/10 hover:bg-amber-500/10 hover:border-amber-500/30 transition-all group">
            <Eye className="w-6 h-6 text-amber-400 mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-amber-200/80 text-xs">Voyance</span>
          </a>
          <a href="#services" className="flex flex-col items-center p-4 rounded-xl bg-amber-500/5 border border-amber-500/10 hover:bg-amber-500/10 hover:border-amber-500/30 transition-all group">
            <Shield className="w-6 h-6 text-amber-400 mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-amber-200/80 text-xs">Protection</span>
          </a>
        </div>

        {/* Bottom Info */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-amber-200/70">
          <a href="#contact" className="flex items-center space-x-2 hover:text-amber-400 transition-colors">
            <MapPin className="w-5 h-5 text-amber-500" />
            <span>Genève</span>
          </a>
          <div className="hidden sm:block w-px h-4 bg-amber-500/30"></div>
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-amber-500" />
            <span>Tous les jours de 8h à 23h30</span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-amber-500/30"></div>
          <a href="tel:+41767836186" className="flex items-center space-x-2 hover:text-amber-400 transition-colors">
            <Phone className="w-5 h-5 text-amber-500" />
            <span className="font-semibold text-amber-400">+41 76 783 61 86</span>
          </a>
        </div>
      </div>

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-neutral-950 to-transparent"></div>
    </section>
  );
};
